import { z } from 'zod';

/**
 * Synthetic test data factory contracts (Delivery roadmap Tier 3b.3).
 *
 * The factory produces deterministic CSV rows from a declarative
 * "recipe" so engineers stop hand-rolling Faker scripts. The recipe
 * is stored alongside the CSV in `data_sources.schema.factory` so a
 * future edit can round-trip the same payload.
 *
 * Why no `@faker-js/faker` dep: Rynora ships browser bundles and the
 * api in a strict-CSP environment; the `faker` library pulls in
 * 70+ locales we don't need. The kinds we support are deliberate
 * and mirrored to the curated wordlists in the api `faker-engine`.
 */

export const DATA_FACTORY_MAX_ROWS = 10_000;
export const DATA_FACTORY_DEFAULT_LOCALE = 'en_US';

export const FactoryColumnKindSchema = z.enum([
  'person.firstName',
  'person.lastName',
  'person.fullName',
  'person.email',
  'person.phone',
  'address.street',
  'address.city',
  'address.country',
  'address.zip',
  'company.name',
  'internet.url',
  'internet.username',
  'date.iso',
  'date.recent',
  'number.int',
  'number.float',
  'string.uuid',
  'string.regex',
  'string.literal',
  'lorem.word',
  'lorem.sentence',
]);
export type FactoryColumnKind = z.infer<typeof FactoryColumnKindSchema>;

export const FactoryColumnSchema = z.object({
  /** Output column name; reused as the CSV header. */
  name: z.string().min(1).max(64),
  kind: FactoryColumnKindSchema,
  /** Per-column locale override; fall back to the recipe's locale. */
  locale: z.string().min(2).max(8).optional(),
  /** When true, the generator rejects duplicate values. */
  unique: z.boolean().optional(),
  /** number.int / number.float lower bound. */
  min: z.number().optional(),
  /** number.int / number.float upper bound. */
  max: z.number().optional(),
  /** Required for `string.regex` — must be a deterministic pattern. */
  pattern: z.string().optional(),
  /** Required for `string.literal` — the constant value to fill. */
  value: z.string().optional(),
});
export type FactoryColumn = z.infer<typeof FactoryColumnSchema>;

export const FactoryRecipeSchema = z.object({
  rows: z.number().int().min(1).max(DATA_FACTORY_MAX_ROWS),
  /** Default locale; per-column `locale` overrides. */
  locale: z.string().min(2).max(8).default(DATA_FACTORY_DEFAULT_LOCALE),
  /**
   * Deterministic seed. Same seed + recipe → identical rows. Omit
   * for a randomised seed (the response echoes the chosen seed so
   * customers can pin reproducibility later).
   */
  seed: z.number().int().nonnegative().optional(),
  columns: z.array(FactoryColumnSchema).min(1).max(64),
});
export type FactoryRecipe = z.infer<typeof FactoryRecipeSchema>;

export const FactoryGenerateRequestSchema = z.object({
  recipe: FactoryRecipeSchema,
  /**
   * When true, the response sticks the recipe + seed onto the named
   * `data_sources` row so future edits round-trip. Frontend usually
   * wires this to the "Save" button.
   */
  persistTo: z
    .object({
      dataSourceName: z.string().min(1).max(120),
    })
    .optional(),
});
export type FactoryGenerateRequest = z.infer<typeof FactoryGenerateRequestSchema>;

export const FactoryGenerateResponseSchema = z.object({
  /** The seed that was used (echoes the request, or fresh-random). */
  seed: z.number().int(),
  /** RFC-4180 CSV body. Same shape as `data_sources.content`. */
  csv: z.string(),
  rows: z.number().int().nonnegative(),
  columns: z.array(z.string()),
  warnings: z.array(z.string()),
});
export type FactoryGenerateResponse = z.infer<typeof FactoryGenerateResponseSchema>;

/* ============================================================
 * Pure helpers — exported for unit tests + the CLI preview.
 * ============================================================ */

/**
 * Mulberry32 PRNG. Tiny, fast, seedable, and the same algorithm
 * the data-grid uses for its in-browser preview so backend +
 * frontend stay in lock-step on the seed.
 */
export function createSeededRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Pick a random integer in `[lo, hi]` (inclusive) using the seeded
 * RNG. `lo` and `hi` may be swapped — we'll normalise.
 */
export function rngInt(rng: () => number, lo: number, hi: number): number {
  const a = Math.min(lo, hi);
  const b = Math.max(lo, hi);
  return Math.floor(rng() * (b - a + 1)) + a;
}

/**
 * Validate a factory recipe in isolation. Returns the list of
 * problems for the caller to surface inline.
 */
export function validateRecipe(recipe: FactoryRecipe): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();
  for (const col of recipe.columns) {
    if (seen.has(col.name)) {
      errors.push(`Duplicate column name: ${col.name}`);
    }
    seen.add(col.name);

    if (col.kind === 'string.regex' && !col.pattern) {
      errors.push(`Column ${col.name}: regex kind requires \`pattern\``);
    }
    if (col.kind === 'string.literal' && col.value === undefined) {
      errors.push(`Column ${col.name}: literal kind requires \`value\``);
    }
    if (
      (col.kind === 'number.int' || col.kind === 'number.float') &&
      col.min !== undefined &&
      col.max !== undefined &&
      col.min > col.max
    ) {
      errors.push(`Column ${col.name}: min > max`);
    }
  }
  return errors;
}
